/** Tabs across the top of a screen (Practice sets, score tables): folder tabs on a shared rule. */

import { textWidth } from '../../render/font';
import { BLACK } from '../../render/palette';
import type { Screen } from '../../render/screen';
import type { Rect } from './menuModel';
import { inRect } from './menuModel';
import { blink, FAINT, INK, MUTED, SELECTED, W } from './theme';

export const TAB_H = 13;

/** The tabs' rectangles, centred across `width`. Pure. */
export function tabRects(labels: readonly string[], y: number, width = W, gap = 6): Rect[] {
  const widths = labels.map((l) => textWidth(l) + 16);
  const total = widths.reduce((a, b) => a + b, 0) + gap * Math.max(0, labels.length - 1);
  let x = Math.floor((width - total) / 2);
  return widths.map((w) => {
    const r = { x, y, w, h: TAB_H };
    x += w + gap;
    return r;
  });
}

export function tabAt(rects: readonly Rect[], x: number, y: number): number {
  return rects.findIndex((r) => inRect(r, x, y));
}

/**
 * `focused`: the tabs themselves have the keyboard (arrows switch tabs), so the active one is
 * yellow and its arrows blink.
 */
export function drawTabs(
  screen: Screen, labels: readonly string[], active: number, rects: readonly Rect[], focused: boolean, ms: number,
  hot = -1,
): void {
  const first = rects[0];
  if (!first) return;
  const base = first.y + TAB_H - 1;
  screen.fill(4, base, screen.width - 8, 1, FAINT);
  labels.forEach((label, i) => {
    const r = rects[i];
    if (!r) return;
    const on = i === active;
    if (on) {
      const frame = focused ? SELECTED : INK;
      screen.fill(r.x, r.y, r.w, TAB_H, BLACK);
      screen.fill(r.x, r.y, r.w, 1, frame);
      screen.fill(r.x, r.y, 1, TAB_H, frame);
      screen.fill(r.x + r.w - 1, r.y, 1, TAB_H, frame);
      screen.fill(r.x + 1, base, r.w - 2, 1, BLACK);
    }
    const colour = on ? (focused ? SELECTED : INK) : i === hot ? INK : MUTED;
    screen.text(label, r.x + r.w / 2, r.y + 3, colour, 'center');
    if (on && focused && blink(ms, 700)) {
      if (i > 0) screen.text('◄', r.x - 9, r.y + 3, SELECTED);
      if (i < labels.length - 1) screen.text('►', r.x + r.w + 1, r.y + 3, SELECTED);
    }
  });
}
